/**
 * Cyberplex API Client - Central API wrapper with versioning support
 *
 * Provides a standardized interface for making API calls to the Cyberplex Portal.
 * Automatically handles:
 * - API versioning via Accept header and URL prefix
 * - CSRF token management
 * - Response envelope parsing
 * - Error handling with standardized error objects
 * - Optional runtime validation with Zod schemas
 *
 * @version 1.1.0
 */

import type { ApiResponse, ApiError } from './types/api';
import type { ZodType } from 'zod';

/**
 * API Client configuration options
 */
interface ApiClientOptions {
  /** Base URL for API calls (default: '/api/v1') */
  baseUrl?: string;
  /** CSRF token for authenticated requests */
  csrfToken?: string | null;
  /** API version to use (default: 'v1') */
  apiVersion?: string;
  /** Enable validation warnings in development (default: false) */
  enableValidationWarnings?: boolean;
}

/**
 * Standardized API error with additional properties
 */
interface ApiErrorExtended extends Error {
  status: number;
  code?: string | null;
  data?: unknown;
  apiVersion?: string;
  requestId?: string;
  csrfToken?: string;
  response?: Response;
}

/**
 * Query parameters for GET requests
 */
type QueryParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Request body data for POST/PUT/PATCH requests
 */
type RequestBody = Record<string, unknown>;

(function() {
  'use strict';

/**
 * Cyberplex API Client class
 */
class CyberplexAPI {
  private baseUrl: string;
  private csrfToken: string | null;
  private apiVersion: string;
  private defaultHeaders: Record<string, string>;
  private enableValidationWarnings: boolean;

  /**
   * Create a new API client instance
   *
   * @param options - Configuration options
   */
  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '/api/v1';
    this.csrfToken = options.csrfToken ?? (typeof window !== 'undefined' ? window.CSRF_TOKEN ?? null : null);
    this.apiVersion = options.apiVersion ?? 'v1';
    this.enableValidationWarnings = options.enableValidationWarnings ?? false;

    // Default headers for all requests
    this.defaultHeaders = {
      'Accept': `application/vnd.cyberplex.${this.apiVersion}+json`,
      'X-API-Version': this.apiVersion.replace('v', ''),
    };

    // Add CSRF token if available
    if (this.csrfToken) {
      this.defaultHeaders['X-CSRF-Token'] = this.csrfToken;
    }
  }

  /**
   * Validate response data against a Zod schema
   *
   * @private
   * @param data - Raw response data
   * @param schema - Zod schema to validate against
   * @returns Validated data
   * @throws Error if validation fails and warnings are enabled
   */
  private _validateResponse<T>(data: unknown, schema: ZodType<T>): T {
    const result = schema.safeParse(data);
    
    if (!result.success) {
      // Log validation errors for debugging
      console.error('[API] Response validation failed:', {
        endpoint: this.baseUrl,
        errors: result.error.format(),
        issues: result.error.issues,
      });

      // In development or when warnings are enabled, log detailed info
      if (this.enableValidationWarnings || (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development')) {
        console.warn('[API] Validation details:', {
          data: data,
          error: result.error.message,
        });
      }

      // Throw error to prevent invalid data from propagating
      throw new Error(`API response validation failed: ${result.error.message}`);
    }

    return result.data;
  }

  /**
   * Update the CSRF token (e.g., after session refresh)
   *
   * @param token - New CSRF token
   */
  setCsrfToken(token: string): void {
    this.csrfToken = token;
    this.defaultHeaders['X-CSRF-Token'] = token;
    if (typeof window !== 'undefined' && window.CSRF_TOKEN !== undefined) {
      window.CSRF_TOKEN = token;
    }
  }

  /**
   * Make a GET request
   *
   * @param endpoint - API endpoint (e.g., '/company-list')
   * @param params - Query parameters
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async get<T>(endpoint: string, params: QueryParams = {}, schema?: ZodType<T>): Promise<T> {
    // Build URL safely without new URL() - avoids Safari compatibility issues
    let url = this.baseUrl + endpoint;

    // Append query parameters if any
    const paramEntries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null);
    if (paramEntries.length > 0) {
      const searchParams = new URLSearchParams();
      paramEntries.forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += '?' + searchParams.toString();
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this.defaultHeaders,
      credentials: 'same-origin',
    });

    const data = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(data, schema);
    }
    
    return data;
  }

  /**
   * Make a POST request
   *
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async post<T>(endpoint: string, data: RequestBody = {}, schema?: ZodType<T>): Promise<T> {
    const response = await fetch(this.baseUrl + endpoint, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'same-origin',
    });

    const responseData = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(responseData, schema);
    }
    
    return responseData;
  }

  /**
   * Make a PUT request
   *
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async put<T>(endpoint: string, data: RequestBody = {}, schema?: ZodType<T>): Promise<T> {
    const response = await fetch(this.baseUrl + endpoint, {
      method: 'PUT',
      headers: {
        ...this.defaultHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'same-origin',
    });

    const responseData = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(responseData, schema);
    }
    
    return responseData;
  }

  /**
   * Make a PATCH request
   *
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async patch<T>(endpoint: string, data: RequestBody = {}, schema?: ZodType<T>): Promise<T> {
    const response = await fetch(this.baseUrl + endpoint, {
      method: 'PATCH',
      headers: {
        ...this.defaultHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'same-origin',
    });

    const responseData = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(responseData, schema);
    }
    
    return responseData;
  }

  /**
   * Make a DELETE request
   *
   * @param endpoint - API endpoint
   * @param params - Query parameters
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async delete<T>(endpoint: string, params: QueryParams = {}, schema?: ZodType<T>): Promise<T> {
    // Build URL safely without new URL() - avoids Safari compatibility issues
    let url = this.baseUrl + endpoint;

    // Append query parameters if any
    const paramEntries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null);
    if (paramEntries.length > 0) {
      const searchParams = new URLSearchParams();
      paramEntries.forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += '?' + searchParams.toString();
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.defaultHeaders,
      credentials: 'same-origin',
    });

    const data = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(data, schema);
    }
    
    return data;
  }

  /**
   * Upload files with multipart/form-data
   *
   * @param endpoint - API endpoint
   * @param formData - FormData object containing files and fields
   * @param schema - Optional Zod schema for response validation
   * @returns Response data payload
   * @throws ApiErrorExtended API error with status and data properties
   */
  async upload<T>(endpoint: string, formData: FormData, schema?: ZodType<T>): Promise<T> {
    // Don't set Content-Type header - browser will set it with boundary
    const headers: Record<string, string> = { ...this.defaultHeaders };
    delete headers['Content-Type'];

    const response = await fetch(this.baseUrl + endpoint, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'same-origin',
    });

    const data = await this._handleResponse<T>(response);
    
    // Validate response if schema provided
    if (schema) {
      return this._validateResponse(data, schema);
    }
    
    return data;
  }

  /**
   * Handle API response
   *
   * @private
   * @param response - Fetch Response object
   * @returns Parsed response data
   * @throws ApiErrorExtended Standardized error object
   */
  private async _handleResponse<T>(response: Response): Promise<T> {
    let data: unknown;

    try {
      data = await response.json();
    } catch (error: unknown) {
      // Non-JSON response
      if (!response.ok) {
        const apiError = new Error(`HTTP ${response.status}: ${response.statusText}`) as ApiErrorExtended;
        apiError.status = response.status;
        apiError.data = null;
        apiError.response = response;
        throw apiError;
      }
      return null as T;
    }

    // Check for deprecation warning
    const deprecationHeader = response.headers.get('Deprecation');
    const sunsetHeader = response.headers.get('Sunset');
    const linkHeader = response.headers.get('Link');

    if (deprecationHeader === 'true') {
      console.warn('API Warning: This endpoint is deprecated.', {
        sunset: sunsetHeader,
        successor: linkHeader,
      });
    }

    // Handle v2 envelope format
    if (data && typeof data === 'object' && 'success' in data) {
      const apiResponse = data as ApiResponse<unknown>;

      if (!apiResponse.success) {
        const errorData = apiResponse.error;
        const errorMessage = typeof errorData === 'object' && errorData?.message
          ? errorData.message
          : typeof errorData === 'string'
            ? errorData
            : 'API Error';
        const apiError = new Error(errorMessage) as ApiErrorExtended;
        apiError.status = response.status;
        apiError.code = typeof errorData === 'object' ? errorData.code ?? null : null;
        apiError.data = data;
        apiError.apiVersion = apiResponse.api_version;
        apiError.requestId = apiResponse.meta?.request_id;

        // Handle CSRF token refresh
        if (typeof errorData === 'object' && errorData?.code === 'csrf' && apiResponse.csrf_token) {
          this.setCsrfToken(apiResponse.csrf_token);
          apiError.csrfToken = apiResponse.csrf_token;
        }

        throw apiError;
      }

      // Return the data payload if it exists, otherwise return the whole object
      // This supports both { success: true, data: {...} } and { success: true, message: "..." }
      return (apiResponse.data !== undefined ? apiResponse.data : apiResponse) as T;
    }

    // Legacy format (direct response)
    if (!response.ok) {
      const errorData = data as { error?: string } | undefined;
      const apiError = new Error(errorData?.error ?? `HTTP ${response.status}`) as ApiErrorExtended;
      apiError.status = response.status;
      apiError.data = data;
      throw apiError;
    }

    return data as T;
  }

  /**
   * Check if the API is available
   *
   * @returns Promise resolving to true if API is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health');
      return true;
    } catch (error: unknown) {
      return false;
    }
  }
}

// =====================================================
// Global Instance Export
// =====================================================

if (typeof window !== 'undefined') {
  window.CyberplexAPI = CyberplexAPI;

  // Create default global instance
  window.api = new CyberplexAPI();

  // Auto-refresh CSRF token from meta tag if present
  document.addEventListener('DOMContentLoaded', () => {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    if (csrfMeta && window.api) {
      window.api.setCsrfToken(csrfMeta.content);
    }
  });
}

})();
